import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types'; 
import { useSelector, useDispatch } from "react-redux";
import { getActiveTermsAndConditions } from "store/slices/authSlice";
import {
  Spin,
  Alert,
  Modal,
  Typography,
  Empty,
  Button,
  Row,
  Col,
  Progress,
  Card,
} from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BookOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

/** Matches public marketing pages (e.g. vlm-landing): mint shell, sage type, green accent */
const V = {
  font: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  ink: "#0f172a",
  body: "#1e293b",
  muted: "#64748b",
  sage: "#4b6b56",
  sageDeep: "#2f4a38",
  accent: "#22c55e",
  accentStrong: "#16a34a",
  borderMint: "rgba(187, 247, 208, 0.78)",
  surface: "#ffffff",
  surfaceSoft: "#f8fffb",
  sheetGradient: "linear-gradient(180deg, #ffffff 0%, #f7fcf9 42%, #fcfffd 100%)",
  headerBg: "linear-gradient(180deg, rgba(248,255,251,.98) 0%, rgba(255,255,255,.96) 100%)",
  shadowLift: "0 12px 40px rgba(15, 23, 42, 0.08), 0 4px 14px rgba(34, 197, 94, 0.08)",
  shadowCard: "0 2px 12px rgba(34, 197, 94, 0.06)",
  radius: 14,
  radiusSm: 10,
};

const modalShellStyles = {
  mask: { backdropFilter: "saturate(140%) blur(6px)" },
  content: {
    borderRadius: V.radius,
    overflow: "hidden",
    boxShadow: V.shadowLift,
    fontFamily: V.font,
  },
  header: {
    background: V.headerBg,
    borderBottom: `1px solid ${V.borderMint}`,
    padding: "16px 20px 14px",
    margin: 0,
  },
  body: {
    fontFamily: V.font,
    background: V.sheetGradient,
    color: V.body,
  },
  footer: {
    background: V.surfaceSoft,
    borderTop: `1px solid ${V.borderMint}`,
    padding: "12px 20px 16px",
    margin: 0,
  },
};

const MODAL_CLASS = "voicera-terms-modal";

/**
 * Enhanced content renderer: formats bullet points and paragraphs properly
 */
const renderFormattedContent = (content) => {
  if (!content) return null;

  const lines = content.split('\n').filter(line => line.trim());
  const elements = [];
  let currentList = [];
  let inList = false;

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Detect bullet points (· or - or *)
    const isBulletPoint = /^[·\-\*]\s+/.test(trimmed);
    
    if (isBulletPoint) {
      if (!inList && currentList.length > 0) {
        // Close previous list
        elements.push(
          <ul key={`list-${index}`} style={{ 
            marginBottom: '12px', 
            paddingLeft: '24px',
            listStyleType: 'disc'
          }}>
            {currentList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                {item}
              </li>
            ))}
          </ul>
        );
        currentList = [];
      }
      inList = true;
      const bulletContent = trimmed.replace(/^[·\-\*]\s+/, '');
      currentList.push(bulletContent);
    } else {
      if (inList && currentList.length > 0) {
        // Close current list
        elements.push(
          <ul key={`list-${index}`} style={{ 
            marginBottom: '12px', 
            paddingLeft: '24px',
            listStyleType: 'disc'
          }}>
            {currentList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                {item}
              </li>
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
      
      // Regular paragraph
      if (trimmed) {
        elements.push(
          <p key={`para-${index}`} style={{ 
            marginBottom: '12px', 
            lineHeight: '1.7',
            textAlign: 'justify'
          }}>
            {trimmed}
          </p>
        );
      }
    }
  });

  // Close any remaining list
  if (inList && currentList.length > 0) {
    elements.push(
      <ul key={`list-final`} style={{ 
        marginBottom: '12px', 
        paddingLeft: '24px',
        listStyleType: 'disc'
      }}>
        {currentList.map((item, idx) => (
          <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            {item}
          </li>
        ))}
      </ul>
    );
  }

  return <div>{elements}</div>;
};

/**
 * Helper function to check if a line is a heading (SonarQube fix - reduce cognitive complexity)
 * Enhanced to detect numbered sections like "1. Acceptance of Terms"
 */
const isHeadingLine = (trimmed) => {
  // Check for numbered headings (e.g., "1. Acceptance of Terms")
  const isNumberedHeading = /^\d+\.\s+[A-Z][A-Za-z\s]+/.test(trimmed);
  // Original heading detection
  const isRegularHeading = /^[A-Z][A-Za-z\s]+:?$/.test(trimmed) && 
                           trimmed.length < 80 && 
                           !trimmed.includes('.') &&
                           (trimmed.endsWith(':') || /^[A-Z][A-Za-z\s]+$/.test(trimmed));
  return isNumberedHeading || isRegularHeading;
};

/**
 * Helper function to create a new section (SonarQube fix - reduce cognitive complexity)
 */
const createSection = (title, sectionsLength, content = "") => {
  return {
    id: `section-${sectionsLength}`,
    title: title.replace(':', ''),
    content: content,
    order: sectionsLength + 1
  };
};

/**
 * Helper function to process heading line (SonarQube fix - reduce cognitive complexity)
 * Enhanced to extract title from numbered headings
 */
const processHeadingLine = (trimmed, current, sections) => {
  if (current?.content?.trim()) {
    sections.push(current);
  }
  // Extract title from numbered heading (e.g., "1. Acceptance of Terms" -> "Acceptance of Terms")
  const isNumberedHeading = /^\d+\.\s+/.test(trimmed);
  const title = isNumberedHeading 
    ? trimmed.replace(/^\d+\.\s+/, '').trim()
    : trimmed.replace(':', '').trim();
  return createSection(title, sections.length);
};

/**
 * Helper function to process content line (SonarQube fix - reduce cognitive complexity)
 */
const processContentLine = (trimmed, current) => {
  if (current) {
    current.content += (current.content ? "\n" : "") + trimmed;
    return current;
  }
  // Handle content before first heading
  return createSection("Overview", 0, trimmed);
};

/**
 * Enhanced parser: better detection of headings and content structure (SonarQube fix - reduce cognitive complexity)
 */
const parseContent = (content) => {
  if (!content) return [];

  const lines = content.split("\n").filter(line => line.trim());
  const sections = [];
  let current = null;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    if (isHeadingLine(trimmed)) {
      current = processHeadingLine(trimmed, current, sections);
    } else {
      current = processContentLine(trimmed, current);
    }
  });

  if (current?.content?.trim()) {
    sections.push(current);
  }
  
  return sections;
};

const TermsAndConditions = ({
  visible = false,
  onClose,
  onAccept,
  onDecline,
  showActions = true,
}) => {
  const dispatch = useDispatch();
  const { termsAndConditions, termsLoading, termsError } = useSelector(
    (state) => state.auth
  );
  const [parsedSections, setParsedSections] = useState([]);
  const [activeSection, setActiveSection] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (visible) {
      dispatch(getActiveTermsAndConditions());
    }
  }, [dispatch, visible]);

  // Responsive design detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Scroll tracking for progress indicator
  useEffect(() => {
    const handleScroll = (e) => {
      const container = e.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setScrollProgress(progress);
    };

    const modalBody = document.querySelector(`.${MODAL_CLASS} .ant-modal-body`);
    if (modalBody) {
      modalBody.addEventListener("scroll", handleScroll);
      return () => modalBody.removeEventListener("scroll", handleScroll);
    }
  }, [visible, termsLoading, termsAndConditions?.content]);

  useEffect(() => {
    if (termsAndConditions?.content) {
      const sections = parseContent(termsAndConditions.content);
      setParsedSections(sections);
    }
  }, [termsAndConditions]);

  // // Anchor items for TOC
  // const anchorItems = parsedSections.map((section) => ({
  //   key: section.id,
  //   href: `#${section.id}`,
  //   title: section.title,
  // }));

  // Handlers
  const handleAccept = () => {
    if (onAccept) onAccept(termsAndConditions);
  };
  const handleDecline = () => {
    if (onDecline) onDecline();
  };

  // Enhanced loading state
  if (termsLoading) {
    return (
      <Modal
        className={MODAL_CLASS}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FileTextOutlined style={{ color: V.accent, fontSize: 18 }} />
            <span style={{ fontWeight: 600, color: V.ink, fontSize: 17 }}>Terms and Conditions</span>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose} style={{ borderRadius: V.radiusSm, borderColor: V.borderMint }}>
            Close
          </Button>,
        ]}
        width={isMobile ? "95vw" : 900}
        styles={{
          ...modalShellStyles,
          body: {
            ...modalShellStyles.body,
            padding: "60px 40px",
            textAlign: "center",
          },
        }}
      >
        <div>
          <Spin size="large" style={{ marginBottom: '24px' }} />
          <Typography.Title level={4} style={{ color: V.sage, margin: 0, fontFamily: V.font }}>
            Loading Terms and Conditions...
          </Typography.Title>
          <Typography.Text style={{ color: V.muted, fontFamily: V.font }}>
            Please wait while we fetch the latest terms.
          </Typography.Text>
        </div>
      </Modal>
    );
  }

  // Enhanced error state
  if (termsError) {
    return (
      <Modal
        className={MODAL_CLASS}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <CloseCircleOutlined style={{ color: "#dc2626" }} />
            <span style={{ fontWeight: 600, color: V.ink, fontSize: 17 }}>Terms and Conditions</span>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={[
          <Button
            key="retry"
            type="primary"
            onClick={() => dispatch(getActiveTermsAndConditions())}
            style={{
              borderRadius: V.radiusSm,
              background: `linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)`,
              border: "none",
              boxShadow: "0 6px 16px rgba(37,99,235,.2)",
            }}
          >
            Try Again
          </Button>,
          <Button key="close" onClick={onClose} style={{ borderRadius: V.radiusSm, borderColor: V.borderMint }}>
            Close
          </Button>,
        ]}
        width={isMobile ? "95vw" : 900}
        styles={{
          ...modalShellStyles,
          body: { ...modalShellStyles.body, padding: "40px" },
        }}
      >
        <Alert
          message="Unable to Load Terms and Conditions"
          description={
            <div>
              <p>We encountered an issue while loading the terms and conditions.</p>
              <p style={{ margin: "8px 0 0 0", fontSize: "13px", color: V.muted }}>
                Error: {typeof termsError === 'string' ? termsError : 'Network connection failed'}
              </p>
            </div>
          }
          type="error"
          showIcon
          style={{
            borderRadius: V.radiusSm,
            border: "1px solid rgba(252, 165, 165, 0.6)",
            background: "#fff",
          }}
        />
      </Modal>
    );
  }

  // Empty state
  if (
    !termsAndConditions?.is_active ||
    !termsAndConditions?.content ||
    parsedSections.length === 0
  ) {
    return (
      <Modal
        className={MODAL_CLASS}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <FileTextOutlined style={{ color: V.accent, fontSize: 18 }} />
            <span style={{ fontWeight: 600, color: V.ink, fontSize: 17 }}>Terms and Conditions</span>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose} style={{ borderRadius: V.radiusSm, borderColor: V.borderMint }}>
            Close
          </Button>,
        ]}
        width={900}
        styles={{
          ...modalShellStyles,
          body: { ...modalShellStyles.body, padding: "48px 24px" },
        }}
      >
        <Empty
          description={
            <span style={{ color: V.sage, fontFamily: V.font }}>
              No terms and conditions available.
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Modal>
    );
  }

  const pill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 999,
    fontFamily: V.font,
    border: `1px solid ${V.borderMint}`,
    lineHeight: 1.2,
  };

  return (
    <Modal
      className={MODAL_CLASS}
      title={
        <div style={{ fontFamily: V.font }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <FileTextOutlined style={{ color: V.accent, fontSize: 18, flexShrink: 0 }} />
              <span style={{ fontSize: 17, fontWeight: 600, color: V.ink }}>Terms and Conditions</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {termsAndConditions.created_at && (
                <span style={{ ...pill, background: V.surfaceSoft, color: V.sageDeep }}>
                  <CalendarOutlined style={{ color: V.accent }} />
                  Last Updated: {dayjs(termsAndConditions.created_at).format("MMM DD, YYYY")}
                </span>
              )}
              {termsAndConditions.version && (
                <span style={{ ...pill, background: "rgba(236, 253, 245, 0.95)", color: V.sageDeep }}>
                  v{termsAndConditions.version}
                </span>
              )}
            </div>
          </div>

          {scrollProgress > 0 && (
            <div style={{ marginTop: 10 }}>
              <Progress
                percent={Math.round(scrollProgress)}
                size="small"
                strokeColor={{ "0%": V.accent, "100%": V.accentStrong }}
                trailColor="rgba(187, 247, 208, 0.35)"
                format={(percent) => `${percent}% read`}
                style={{ marginBottom: 0 }}
              />
            </div>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: isMobile ? "4px 0 0" : "6px 0 0",
            fontFamily: V.font,
          }}
        >
          {isMobile && scrollProgress > 0 && (
            <div style={{ flex: 1, marginRight: 16 }}>
              <Typography.Text style={{ fontSize: 12, color: V.muted }}>
                {Math.round(scrollProgress)}% read
              </Typography.Text>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
            {showActions ? [
              <Button
                key="decline"
                danger
                onClick={handleDecline}
                icon={<CloseCircleOutlined />}
                size={isMobile ? "large" : "middle"}
                style={{ minWidth: isMobile ? 120 : "auto", borderRadius: V.radiusSm }}
              >
                Decline
              </Button>,
              <Button
                key="accept"
                type="primary"
                onClick={handleAccept}
                icon={<CheckCircleOutlined />}
                size={isMobile ? "large" : "middle"}
                style={{
                  minWidth: isMobile ? 120 : "auto",
                  borderRadius: V.radiusSm,
                  background: "linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)",
                  border: "none",
                  boxShadow: "0 6px 16px rgba(37,99,235,.2)",
                }}
              >
                Accept
              </Button>,
            ] : [
              <Button
                key="close"
                onClick={onClose}
                size={isMobile ? "large" : "middle"}
                style={{
                  minWidth: isMobile ? 120 : "auto",
                  borderRadius: V.radiusSm,
                  borderColor: V.borderMint,
                  color: V.sageDeep,
                  background: V.surface,
                }}
              >
                Close
              </Button>,
            ]}
          </div>
        </div>
      }
      width={isMobile ? "95vw" : 1000}
      styles={{
        ...modalShellStyles,
        body: {
          ...modalShellStyles.body,
          maxHeight: isMobile ? "70vh" : "75vh",
          overflowY: "auto",
          padding: isMobile ? 16 : 24,
        },
      }}
      centered
    >
      {/* Content Layout - Responsive */}
      {isMobile ? (
        <div style={{ fontFamily: V.font }}>
          <Card
            size="small"
            style={{
              marginBottom: 20,
              borderRadius: V.radiusSm,
              border: `1px solid ${V.borderMint}`,
              boxShadow: V.shadowCard,
              background: V.surface,
            }}
          >
            <Typography.Title
              level={5}
              style={{
                margin: "0 0 12px 0",
                color: V.sageDeep,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFamily: V.font,
              }}
            >
              <BookOutlined style={{ color: V.accent }} />
              Table of Contents
              <span style={{ ...pill, padding: "2px 8px", fontSize: 11 }}>{parsedSections.length}</span>
            </Typography.Title>
            <div style={{ maxHeight: 120, overflowY: "auto" }}>
              {parsedSections.map((section, index) => (
                <div key={section.id} style={{ marginBottom: 8 }}>
                  <a
                    href={`#${section.id}`}
                    style={{
                      fontSize: 13,
                      color: activeSection === section.id ? V.sageDeep : V.muted,
                      textDecoration: "none",
                      display: "block",
                      padding: "6px 10px",
                      borderRadius: 8,
                      background: activeSection === section.id ? "rgba(34, 197, 94, 0.1)" : "transparent",
                      border: `1px solid ${activeSection === section.id ? V.borderMint : "transparent"}`,
                      fontWeight: activeSection === section.id ? 600 : 400,
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                      setActiveSection(section.id);
                    }}
                  >
                    {index + 1}. {section.title}
                  </a>
                </div>
              ))}
            </div>
          </Card>

          <div>
            {parsedSections.map((section, index) => (
              <Card
                key={section.id}
                id={section.id}
                style={{
                  marginBottom: 16,
                  borderRadius: V.radiusSm,
                  border: `1px solid ${V.borderMint}`,
                  boxShadow: V.shadowCard,
                  background: V.surface,
                }}
              >
                <Typography.Title
                  level={5}
                  style={{
                    color: V.sageDeep,
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: V.font,
                  }}
                >
                  <span
                    style={{
                      background: `linear-gradient(135deg, ${V.accent} 0%, ${V.accentStrong} 100%)`,
                      color: "white",
                      borderRadius: 8,
                      width: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      boxShadow: "0 2px 8px rgba(34, 197, 94, 0.25)",
                    }}
                  >
                    {index + 1}
                  </span>
                  {section.title}
                </Typography.Title>
                <div
                  style={{
                    lineHeight: 1.7,
                    color: V.body,
                    fontSize: 14,
                    marginBottom: 0,
                    fontFamily: V.font,
                  }}
                >
                  {renderFormattedContent(section.content)}
                </div>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <Row gutter={[20, 20]} style={{ fontFamily: V.font }}>
          <Col span={6}>
            <Card
              style={{
                position: "sticky",
                top: 0,
                borderRadius: V.radiusSm,
                border: `1px solid ${V.borderMint}`,
                boxShadow: V.shadowCard,
                background: `linear-gradient(180deg, ${V.surface} 0%, ${V.surfaceSoft} 100%)`,
              }}
              bodyStyle={{ padding: 18 }}
            >
              <Typography.Title
                level={5}
                style={{
                  marginBottom: 14,
                  color: V.sageDeep,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  fontFamily: V.font,
                }}
              >
                <BookOutlined style={{ color: V.accent }} />
                Table of Contents
                <span style={{ ...pill, padding: "2px 9px", fontSize: 11 }}>{parsedSections.length}</span>
              </Typography.Title>

              <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
                {parsedSections.map((section, index) => (
                  <div key={section.id} style={{ marginBottom: 10 }}>
                    <a
                      href={`#${section.id}`}
                      style={{
                        display: "block",
                        padding: "10px 14px",
                        borderRadius: V.radiusSm,
                        textDecoration: "none",
                        fontSize: 13,
                        fontWeight: 500,
                        color: activeSection === section.id ? V.sageDeep : V.muted,
                        background: activeSection === section.id ? "rgba(34, 197, 94, 0.08)" : "transparent",
                        border: `1px solid ${activeSection === section.id ? V.borderMint : "transparent"}`,
                        transition: "background 0.2s ease, border-color 0.2s ease",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                        setActiveSection(section.id);
                      }}
                    >
                      <span style={{ marginRight: 8, color: V.accent, fontWeight: 700 }}>{index + 1}.</span>
                      {section.title}
                    </a>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col span={18}>
            <div style={{ paddingLeft: 4 }}>
              {parsedSections.map((section, index) => (
                <Card
                  key={section.id}
                  id={section.id}
                  style={{
                    marginBottom: 20,
                    borderRadius: V.radiusSm,
                    border: `1px solid ${V.borderMint}`,
                    boxShadow: V.shadowCard,
                    background: V.surface,
                  }}
                  bodyStyle={{ padding: "22px 24px" }}
                >
                  <Typography.Title
                    level={4}
                    style={{
                      color: V.sageDeep,
                      marginBottom: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      paddingBottom: 12,
                      borderBottom: `1px solid rgba(187, 247, 208, 0.65)`,
                      fontFamily: V.font,
                      fontSize: 17,
                    }}
                  >
                    <span
                      style={{
                        background: `linear-gradient(135deg, ${V.accent} 0%, ${V.accentStrong} 100%)`,
                        color: "white",
                        borderRadius: V.radiusSm,
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 700,
                        boxShadow: "0 4px 12px rgba(34, 197, 94, 0.22)",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ lineHeight: 1.35 }}>{section.title}</span>
                  </Typography.Title>
                  <div
                    style={{
                      lineHeight: 1.75,
                      color: V.body,
                      fontSize: 15,
                      marginBottom: 0,
                      fontFamily: V.font,
                    }}
                  >
                    {renderFormattedContent(section.content)}
                  </div>
                </Card>
              ))}
            </div>
          </Col>
        </Row>
      )}
    </Modal>
  );
};
TermsAndConditions.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAccept: PropTypes.func,
  onDecline: PropTypes.func,
  showActions: PropTypes.bool,
};

TermsAndConditions.defaultProps = {
  visible: false,
  onAccept: undefined,
  onDecline: undefined,
  showActions: true,
};
export default TermsAndConditions;
